import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SmartLink } from "./SmartLink";
import { GraduationCap, Crown, Menu, X, ChevronDown } from "lucide-react";
import { LOGO_URL } from "../data/content";
import CommunityBar from "./CommunityBar";
import Breadcrumbs from "./Breadcrumbs";

const NAV = [
  { label: "Servizi", to: "/servizi" },
  { label: "Corsi", to: "/corsi" },
  { label: "Cantiere Host", to: "/cantierehost" },
  { label: "Blog", to: "/blog" },
  { label: "Negozio", dropdown: "negozio" },
  { label: "App", dropdown: "app" },
  { label: "Contatti", dropdown: "contatti" },
  { label: "Newsletter", to: "/newsletter" },
];

const NEGOZIO_LINKS = [
  { name: "Welcome Book Digitale", sub: "1 link · tutte le info ospite · Hostland", to: "/guestbook-digitale" },
  { name: "HappyHour", sub: "Codici sconto Hostland a sorteggio", to: "/happyhour" },
  { name: "FaqBnb — il libro", sub: "Bestseller gratuito per iniziare", to: "/servizi/faqbnb" },
  { name: "Cartelli", sub: "Recensioni e cartelli casa stampabili", to: "/cartelli" },
  { name: "Offerte Amazon", sub: "Selezionate per host, aggiornate", to: "/negozio/offerte-amazon" },
  { name: "Codici Sconto", sub: "Sconti riservati agli host", to: "/negozio/codici-sconto" },
  { name: "Zerbini Divertenti", sub: "L'ironia prima del check-in", to: "/negozio/zerbini-divertenti" },
  { name: "Servizio stampa", sub: "Magliette, mug, poster, gadget", to: "/negozio#servizio-stampa" },
  { name: "Stampa 3D", sub: "MagiKey, targhe CIN, gadget 3D", to: "/negozio#biglietti-stampa-3d" },
  { name: "Tutto il negozio", sub: "Vai alla pagina completa", to: "/negozio" },
];

const CONTATTI_LINKS = [
  { name: "Contatti", sub: "Scrivici o chiamaci", to: "/contatti" },
  { name: "Lavora con noi", sub: "Collabora e partnership", to: "/contatti/collabora" },
  { name: "Segnalazione", sub: "Segnala un problema", to: "/contatti/segnalazione" },
];

const APP_LINKS = [
  { name: "HostSales", sub: "Offerte Amazon per host", to: "/offerte", icon: "/app-icons/hostsales.webp" },
  { name: "HostRevenue", sub: "Analisi ricavi e pricing", to: "/servizi/hostrevenue", icon: "/app-icons/hostrevenue.webp" },
  { name: "Vita da Host", sub: "Webapp ufficiale", to: "/servizi/app-ufficiali", icon: "/app-icons/vitadahost.webp" },
  { name: "Scelto da Vita da Host", sub: "Strutture selezionate", to: "/servizi/scelto-da-vita-da-host", icon: "/app-icons/scelto.webp" },
  { name: "FaqBnb", sub: "Il manuale 2026", to: "/servizi/faqbnb", icon: "/app-icons/faqbnb.webp" },
  { name: "Hostland", sub: "Welcome book e sito senza commissioni", to: "/hostland", icon: "/app-icons/hostland.webp" },
  { name: "HostMarket", sub: "Marketplace dei pro", to: "/servizi/hostmarket", icon: "/app-icons/hostmarket.webp" },
  { name: "LocazioneTuristica", sub: "Tool host privati", to: "/servizi/lt", icon: "/app-icons/locazioneturistica.webp" },
  { name: "WCity", sub: "Bagni pubblici", to: "/tools/bagni-pubblici", icon: "/app-icons/wcity.webp" },
  { name: "QuantoGuadagni?", sub: "Netto Airbnb e Booking", to: "/calcolatoreguadagniaffittibrevi", icon: "/app-icons/quantoguadagni.webp" },
  { name: "Radar dei vettori", sub: "Newsletter voli e traghetti", to: "/radar", icon: "/app-icons/radar.webp" },
  { name: "Cody", sub: "Generatore QR", to: "/qrcodegratis", icon: "/app-icons/cody.webp" },
  { name: "CalculHost", sub: "Imposta di soggiorno", to: "/strumenti/calcolo-imposta-soggiorno", icon: "/app-icons/calculhost.webp" },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const [mDrop, setMDrop] = useState(null);

  return (
    <>
      <header
        className="sticky top-0 z-50 font-figtree vdh-safe-top"
        style={{ background: "#050505", borderBottom: "1px solid #22252C" }}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-[1280px] px-4 md:px-8 h-[72px] flex items-center gap-4 lg:gap-7">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo" aria-label="Vita da Host">
            <img src={LOGO_URL} alt="Vita da Host" className="h-[66px] md:h-[80px] w-auto object-contain" />
          </Link>

          {/* Desktop nav */}
          <nav aria-label="Principale" className="hidden lg:flex items-center gap-[22px] text-[15px] font-semibold flex-1">
            {NAV.map((n) => {
              if (!n.dropdown) {
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    className="vdh-nav-link transition-colors"
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {n.label}
                  </Link>
                );
              }
              const open = openDrop === n.dropdown;
              const isApp = n.dropdown === "app";
              const items = isApp ? APP_LINKS : n.dropdown === "negozio" ? NEGOZIO_LINKS : CONTATTI_LINKS;
              return (
                <div key={n.label} className="relative" onMouseLeave={() => setOpenDrop(null)}>
                  <button
                    type="button"
                    onClick={() => setOpenDrop(open ? null : n.dropdown)}
                    onMouseEnter={() => setOpenDrop(n.dropdown)}
                    aria-expanded={open}
                    aria-haspopup="true"
                    className="vdh-nav-link transition-colors inline-flex items-center gap-1"
                    data-testid={`nav-${n.dropdown}`}
                  >
                    {n.label} <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div
                      className="absolute left-0 top-full pt-3 z-50"
                      onMouseEnter={() => setOpenDrop(n.dropdown)}
                    >
                      <div
                        className={`rounded-2xl p-3 ${isApp ? "w-[520px] grid grid-cols-2 gap-1.5" : "w-[300px] flex flex-col gap-1"}`}
                        style={{ background: "#FFFFFF", border: "1px solid #E4E6EA", boxShadow: "0 24px 60px -20px rgba(0,0,0,0.35)" }}
                        data-testid={`nav-${n.dropdown}-dropdown`}
                      >
                        {items.map((a) => (
                          <SmartLink
                            key={a.name}
                            to={a.to}
                            onClick={() => setOpenDrop(null)}
                            className="flex items-center gap-3 rounded-xl p-2.5 transition-colors hover:bg-[#F6F7F9]"
                            data-testid={`nav-${n.dropdown}-item-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            {a.icon && <img src={a.icon} alt="" width={40} height={40} decoding="async" className="w-10 h-10 rounded-[10px] object-cover flex-none" />}
                            <span className="min-w-0">
                              <span className="block text-[14px] font-bold truncate" style={{ color: "#111318" }}>{a.name}</span>
                              <span className="block text-[12px] truncate" style={{ color: "#5B616C" }}>{a.sub}</span>
                            </span>
                          </SmartLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Desktop CTAs — grouped tight together */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <Link
              to="/hostlab"
              data-testid="nav-hostlab"
              className="flex h-11 px-3.5 rounded-xl items-center gap-2 font-extrabold text-[15px] text-white transition-transform hover:-translate-y-0.5"
              style={{ background: "#7C3AED" }}
            >
              <GraduationCap size={19} /> HostLab
            </Link>
            <Link
              to="/hostclub"
              data-testid="nav-hostclub"
              className="flex h-11 px-3.5 rounded-xl items-center gap-2 font-extrabold text-[15px] transition-transform hover:-translate-y-0.5"
              style={{ background: "#EA580C", color: "#FFFFFF", boxShadow: "inset 0 0 0 1px #F2D27A" }}
            >
              <Crown size={19} /> HostClub
            </Link>
            <Link
              to="/consulenze"
              data-testid="nav-consulenze-cta"
              className="flex h-11 px-4 rounded-xl items-center font-extrabold text-[15px] transition-transform hover:-translate-y-0.5"
              style={{ background: "#FFFFFF", color: "#111318" }}
            >
              Consulenze
            </Link>
          </div>

          {/* Mobile controls */}
          <div className="flex lg:hidden items-center gap-2 ml-auto">
            <button
              type="button"
              aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
              onClick={() => setMenuOpen((v) => !v)}
              className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ border: "1px solid #3A3F4B", background: "#050505" }}
              data-testid="nav-menu-btn"
            >
              {menuOpen ? <X size={20} className="text-white" /> : <Menu size={20} className="text-white" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div
            className="lg:hidden px-5 py-4 overflow-y-auto overscroll-contain"
            style={{
              borderTop: "1px solid rgba(255,255,255,0.08)",
              background: "linear-gradient(160deg, rgba(14,15,20,0.97) 0%, rgba(6,6,9,0.98) 100%)",
              backdropFilter: "blur(22px) saturate(140%)",
              WebkitBackdropFilter: "blur(22px) saturate(140%)",
              boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
              maxHeight: "calc(100dvh - 72px - env(safe-area-inset-top))",
              WebkitOverflowScrolling: "touch",
              paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
            }}
            data-testid="nav-mobile-menu"
          >
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Link to="/hostlab" onClick={() => setMenuOpen(false)} className="h-11 rounded-xl flex items-center justify-center gap-1.5 font-extrabold text-[14px] text-white" style={{ background: "#7C3AED" }}>
                <GraduationCap size={17} /> HostLab
              </Link>
              <Link to="/hostclub" onClick={() => setMenuOpen(false)} className="h-11 rounded-xl flex items-center justify-center gap-1.5 font-extrabold text-[14px]" style={{ background: "#EA580C", color: "#FFFFFF", boxShadow: "inset 0 0 0 1px #F2D27A" }}>
                <Crown size={17} /> HostClub
              </Link>
              <Link to="/consulenze" onClick={() => setMenuOpen(false)} className="h-11 rounded-xl flex items-center justify-center font-extrabold text-[14px]" style={{ background: "#FFFFFF", color: "#111318" }}>
                Consulenze
              </Link>
            </div>
            <nav className="flex flex-col gap-0">
              {NAV.filter((n) => !n.dropdown).map((n) => (
                <Link
                  key={n.to}
                  to={n.label === "Servizi" ? "/#percorsi" : n.to}
                  onClick={() => setMenuOpen(false)}
                  className="font-display font-bold text-[24px] tracking-tight text-white leading-tight py-1 px-2 rounded-lg hover:bg-white/5"
                  data-testid={`nav-m-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </Link>
              ))}
              <div className="grid grid-cols-2 gap-0.5 mt-2">
                {APP_LINKS.map((a) => (
                  <SmartLink
                    key={a.name}
                    to={a.to}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 py-1 px-2 rounded-lg hover:bg-white/5"
                    data-testid={`nav-m-app-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <img src={a.icon} alt="" width={48} height={48} decoding="async" className="w-12 h-12 rounded-[10px] object-cover flex-none" />
                    <span className="text-white text-[14px] font-semibold truncate">{a.name}</span>
                  </SmartLink>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setMDrop((v) => (v === "negozio" ? null : "negozio"))}
                className="flex items-center justify-between font-display font-bold text-[24px] tracking-tight text-white leading-tight py-1 px-2 mt-1.5 rounded-lg hover:bg-white/5"
                data-testid="nav-m-drop-negozio"
              >
                Negozio
                <ChevronDown size={22} className={`transition-transform ${mDrop === "negozio" ? "rotate-180" : ""}`} />
              </button>
              {mDrop === "negozio" && NEGOZIO_LINKS.map((a) => (
                <Link key={a.name} to={a.to} onClick={() => setMenuOpen(false)} className="text-white/80 text-[15px] py-1.5 px-4 rounded-lg hover:bg-white/5">{a.name}</Link>
              ))}

              <button
                type="button"
                onClick={() => setMDrop((v) => (v === "contatti" ? null : "contatti"))}
                className="flex items-center justify-between font-display font-bold text-[24px] tracking-tight text-white leading-tight py-1 px-2 rounded-lg hover:bg-white/5"
                data-testid="nav-m-drop-contatti"
              >
                Contatti
                <ChevronDown size={22} className={`transition-transform ${mDrop === "contatti" ? "rotate-180" : ""}`} />
              </button>
              {mDrop === "contatti" && CONTATTI_LINKS.map((a) => (
                <Link key={a.name} to={a.to} onClick={() => setMenuOpen(false)} className="text-white/80 text-[15px] py-1.5 px-4 rounded-lg hover:bg-white/5">{a.name}</Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      <CommunityBar />
      <Breadcrumbs />
    </>
  );
}
